import { useTranslation } from "react-i18next";

import { Arrange, CloseButton, Drawer, Typography } from "@aviary";
import type { HeaderType } from "@aviary/components/Typography/Typography.types";
import { Direction } from "@aviary/types";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import type { SearchApplyEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { CloseFilterEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { l } from "@shared/locales/i18n";
import { DrawerAnalyticTitles } from "@shared/types/OverlayAnalyticTitles";

import { ClearFiltersButton } from "../ClearFiltersButton/ClearFiltersButton";
import { Sort } from "../Filters/Sort/Sort";
import { FiltersContent } from "../FiltersContent/FiltersContent";

import { MobileFilterFooter } from "./MobileFilterFooter/MobileFilterFooter";

import * as styles from "./MobileFilterDrawer.styles";

interface Props {
  onClearFilters: () => void;
  resultCount: number;
  isCyberMondayEnabled?: boolean;
  isCatalogDrawer?: boolean;
  entryPointForLogging?: SearchApplyEntryPoint;
  eventNameForLogging?: AnalyticsEvent;
  isSticky?: boolean;
  showClearFilterButton?: boolean;
  showSort?: boolean;
  titleType?: HeaderType;
  isLoading: boolean;
}

const MobileFilterDrawer = ({
  onClearFilters,
  resultCount,
  isCatalogDrawer = false,
  entryPointForLogging,
  eventNameForLogging = AnalyticsEvent.STORE_SEARCH_APPLY_FILTER,
  isCyberMondayEnabled = false,
  isSticky,
  showClearFilterButton,
  showSort = true,
  titleType = "h2",
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const { activeSearchType, setActiveSearchType } = useCatalogSearch();
  const { tablet, desktop, widescreen } = useBreakpoints();
  const showFilters = activeSearchType === "filters";
  const logAnalyticsEvent = useAnalyticsLogger();
  const { isUnauthenticated, isEmerson } = useSharedGlobalConfig();
  const smallHeader = titleType === "h3";

  let searchCloseFilterEvent: AnalyticsEvent;

  if (isEmerson && isUnauthenticated) {
    searchCloseFilterEvent = AnalyticsEvent.EMERSON_UNAUTH_SEARCH_CLOSE_FILTER;
  } else if (isEmerson) {
    searchCloseFilterEvent = AnalyticsEvent.EMERSON_SEARCH_CLOSE_FILTER;
  } else {
    searchCloseFilterEvent = AnalyticsEvent.STORE_SEARCH_CLOSE_FILTER;
  }

  const logCloseFilterEvent = () => {
    if (!entryPointForLogging) return;
    if (isUnauthenticated && !isEmerson) return;

    logAnalyticsEvent({
      eventName: searchCloseFilterEvent,
      payload: {
        entry_point: CloseFilterEntryPoint[`${entryPointForLogging}_Catalog_FilterCloseButton`],
      },
    });
  };

  const onCloseButtonClick = () => {
    setActiveSearchType("");
    logCloseFilterEvent();
  };

  const getOpensFrom = () => (tablet.lessThan ? Direction.BOTTOM : Direction.RIGHT);

  const getBreakpoints = () => {
    if (isCatalogDrawer) {
      return widescreen.lessThan;
    }

    return desktop.lessThan;
  };

  const clearFilters = showClearFilterButton && (
    <ClearFiltersButton onClearFilters={onClearFilters} withMargin />
  );

  if (getBreakpoints()) {
    return (
      <Drawer
        isOpen={showFilters}
        css={styles.dialog}
        hasBackdrop={false}
        opensFrom={getOpensFrom()}
        analyticsTitle={DrawerAnalyticTitles.CATALOG_SEARCH_MOBILE_FILTER}
      >
        <div css={[styles.container, isSticky && styles.stickyContainer]}>
          <Arrange
            justify="spaceBetween"
            css={[
              styles.header,
              isSticky && styles.stickyHeader,
              smallHeader && styles.smallHeader,
            ]}
          >
            <Typography type={titleType}>{t(l.sharedCatalog.MobileFilterHeader)}</Typography>
            <CloseButton
              size="small"
              aria-label={t(l.sharedCatalog.CloseFilter)}
              onClick={onCloseButtonClick}
            />
          </Arrange>
          {!!showSort && <Sort isBold isDropdown={isEmerson} />}
          {clearFilters}
          <div css={styles.accordionFilters}>
            <FiltersContent
              isBold
              isDrawer={isCatalogDrawer}
              isCyberMondayEnabled={isCyberMondayEnabled}
              entryPointForLogging={entryPointForLogging}
              eventNameForLogging={eventNameForLogging}
            />
          </div>
        </div>
        <MobileFilterFooter
          resultCount={resultCount}
          onClearFilters={onClearFilters}
          hideClearAllButton={isEmerson}
          isLoading={isLoading}
        />
      </Drawer>
    );
  }

  return null;
};

export { MobileFilterDrawer };
